<script>
  import { onMount } from "svelte";
  import { user, auth, hasAlpacaAccount } from "../../stores/auth.js";
  import { navigate } from "svelte-routing";

  const API_URL = window.RUNTIME_CONFIG?.API_URL || 'http://localhost:8004';

  let imageError = false;
  
  // Investment settings state
  let settingsLoading = true;
  let settingsSaving = false;
  let settingsError = "";
  let settingsSuccess = "";
  let investmentSettings = {
    auto_invest_enabled: false,
    auto_invest_amount: "",
    auto_rebalance_enabled: false,
  };
  
  // Personal info state
  let personalInfoLoading = true;
  let personalInfoSaving = false;
  let personalInfoError = "";
  let personalInfoSuccess = "";
  let personalInfoEditMode = false;
  let accountInfo = null;
  let editedContact = {
    phone_number: "",
    street_address: "",
    unit: "",
    city: "",
    state: "",
    postal_code: "",
  };
  let editedDisclosures = {
    employment_status: "",
    employer_name: "",
    employer_address: "",
    employment_position: "",
    is_control_person: false,
    is_affiliated_exchange_or_finra: false,
    is_politically_exposed: false,
    immediate_family_exposed: false,
  };
  let editedIdentity = {
    funding_source: [],
  };
  let personalFieldErrors = {};
  
  // Funding sources available
  const fundingSources = [
    { value: "employment_income", label: "Employment Income" },
    { value: "investments", label: "Investments" },
    { value: "business_income", label: "Business Income" },
    { value: "savings", label: "Savings" },
    { value: "inheritance", label: "Inheritance" },
    { value: "family", label: "Family" },
  ];
  
  // US States list for dropdown
  const usStates = [
    { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
    { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "Washington D.C." }
  ];
  
  // ================================
  // Validation Functions (similar to Onboarding)
  // ================================
  
  function validatePhone(phone) {
    if (!phone || !phone.trim()) return null; // Optional field
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length < 10) return "Phone number must be at least 10 digits";
    if (cleaned.length > 15) return "Phone number is too long";
    return null;
  }

  function formatPhone(value) {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }

  function handlePhoneInput(event) {
    editedContact.phone_number = formatPhone(event.target.value);
  }
  
  // Strip +1 prefix from phone number for display (US only)
  function stripPhonePrefix(phone) {
    if (!phone) return "";
    // Remove +1 prefix if present
    let cleaned = phone.replace(/^\+1/, "");
    // Format the number for display
    cleaned = cleaned.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return formatPhone(cleaned);
    }
    return phone;
  }

  function validateAddress(address) {
    if (!address || !address.trim()) return null; // Optional if not changing
    if (address.trim().length < 5) return "Please enter a valid street address";
    // Check for PO Box
    if (/p\.?\s*o\.?\s*box/i.test(address)) return "PO Boxes are not allowed. Please use a physical address.";
    return null;
  }

  function validateCity(city) {
    if (!city || !city.trim()) return null; // Optional if not changing
    if (city.trim().length < 2) return "Please enter a valid city";
    if (!/^[a-zA-Z\s\-'.]+$/.test(city)) return "City contains invalid characters";
    return null;
  }

  function validateState(state) {
    if (!state) return null; // Optional if not changing
    return null;
  }

  function validatePostalCode(code) {
    if (!code || !code.trim()) return null; // Optional if not changing
    if (!/^\d{5}(-\d{4})?$/.test(code)) return "Enter valid ZIP code (12345 or 12345-6789)";
    return null;
  }

  function validatePersonalInfo() {
    personalFieldErrors = {};
    let hasErrors = false;
    
    // Validate contact fields
    const phoneError = validatePhone(editedContact.phone_number);
    if (phoneError) { personalFieldErrors.phone_number = phoneError; hasErrors = true; }
    
    const addressError = validateAddress(editedContact.street_address);
    if (addressError) { personalFieldErrors.street_address = addressError; hasErrors = true; }
    
    const cityError = validateCity(editedContact.city);
    if (cityError) { personalFieldErrors.city = cityError; hasErrors = true; }
    
    const stateError = validateState(editedContact.state);
    if (stateError) { personalFieldErrors.state = stateError; hasErrors = true; }
    
    const postalError = validatePostalCode(editedContact.postal_code);
    if (postalError) { personalFieldErrors.postal_code = postalError; hasErrors = true; }
    
    // Cross-field validation: if any address field is filled, all required ones should be filled
    const addressFields = [editedContact.street_address, editedContact.city, editedContact.state, editedContact.postal_code];
    const anyAddressChanged = addressFields.some(f => f && f.trim());
    
    if (anyAddressChanged) {
      if (!editedContact.street_address || !editedContact.street_address.trim()) {
        personalFieldErrors.street_address = "Street address is required when updating address";
        hasErrors = true;
      }
      if (!editedContact.city || !editedContact.city.trim()) {
        personalFieldErrors.city = "City is required when updating address";
        hasErrors = true;
      }
      if (!editedContact.state) {
        personalFieldErrors.state = "State is required when updating address";
        hasErrors = true;
      }
      if (!editedContact.postal_code || !editedContact.postal_code.trim()) {
        personalFieldErrors.postal_code = "ZIP code is required when updating address";
        hasErrors = true;
      }
    }
    
    return !hasErrors;
  }
  
  // Close account state
  let showCloseModal = false;
  let closureEligibility = null;
  let closureLoading = false;
  let closureError = null;
  let closureSuccess = false;
  let confirmText = "";

  onMount(async () => {
    if ($hasAlpacaAccount) {
      await fetchSettings();
      await fetchPersonalInfo();
    }
  });

  async function fetchSettings() {
    settingsLoading = true;
    settingsError = "";
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_URL}/api/user/settings`, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch settings");
      }
      
      investmentSettings = await response.json();
    } catch (err) {
      console.error("Failed to fetch settings:", err);
      settingsError = err.message;
    } finally {
      settingsLoading = false;
    }
  }

  async function saveSettings() {
    settingsSaving = true;
    settingsError = "";
    settingsSuccess = "";
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_URL}/api/user/settings`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          auto_invest_enabled: investmentSettings.auto_invest_enabled,
          auto_invest_amount: investmentSettings.auto_invest_amount || null,
          auto_rebalance_enabled: investmentSettings.auto_rebalance_enabled,
        }),
      });
      
      if (!response.ok) {
        throw new Error("Failed to save settings");
      }
      
      investmentSettings = await response.json();
      settingsSuccess = "Settings saved successfully!";
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        settingsSuccess = "";
      }, 3000);
    } catch (err) {
      settingsError = err.message;
    } finally {
      settingsSaving = false;
    }
  }

  async function fetchPersonalInfo() {
    personalInfoLoading = true;
    personalInfoError = "";
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_URL}/api/account/info`, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch account information");
      }
      
      accountInfo = await response.json();
      
      // Populate edit fields with current values
      if (accountInfo.contact) {
        editedContact = {
          phone_number: stripPhonePrefix(accountInfo.contact.phone_number),
          street_address: Array.isArray(accountInfo.contact.street_address)
            ? accountInfo.contact.street_address.join(", ")
            : (accountInfo.contact.street_address || ""),
          unit: accountInfo.contact.unit || "",
          city: accountInfo.contact.city || "",
          state: accountInfo.contact.state || "",
          postal_code: accountInfo.contact.postal_code || "",
        };
      }
      
      // Get employment info from employment object (not disclosures)
      if (accountInfo.employment) {
        editedDisclosures.employment_status = accountInfo.employment.status || "";
        editedDisclosures.employer_name = accountInfo.employment.employer_name || "";
        editedDisclosures.employer_address = accountInfo.employment.employer_address || "";
        editedDisclosures.employment_position = accountInfo.employment.job_title || "";
      }
      
      // Get affiliations from disclosures object
      if (accountInfo.disclosures) {
        editedDisclosures.is_control_person = accountInfo.disclosures.is_control_person || false;
        editedDisclosures.is_affiliated_exchange_or_finra = accountInfo.disclosures.is_affiliated_exchange_or_finra || false;
        editedDisclosures.is_politically_exposed = accountInfo.disclosures.is_politically_exposed || false;
        editedDisclosures.immediate_family_exposed = accountInfo.disclosures.immediate_family_exposed || false;
      }
      
      // Get funding source from financial object (not identity)
      if (accountInfo.financial) {
        const fundingSource = accountInfo.financial.funding_source;
        // Convert string to array if needed
        editedIdentity.funding_source = Array.isArray(fundingSource) ? fundingSource : (fundingSource ? [fundingSource] : []);
      }
    } catch (err) {
      console.error("Failed to fetch personal info:", err);
      personalInfoError = err.message;
    } finally {
      personalInfoLoading = false;
    }
  }

  function enableEditMode() {
    personalInfoEditMode = true;
    personalInfoError = "";
    personalInfoSuccess = "";
  }

  function cancelEdit() {
    personalInfoEditMode = false;
    // Reset edit fields to current values
    if (accountInfo?.contact) {
      editedContact = {
        phone_number: stripPhonePrefix(accountInfo.contact.phone_number),
        street_address: Array.isArray(accountInfo.contact.street_address)
          ? accountInfo.contact.street_address.join(", ")
          : (accountInfo.contact.street_address || ""),
        unit: accountInfo.contact.unit || "",
        city: accountInfo.contact.city || "",
        state: accountInfo.contact.state || "",
        postal_code: accountInfo.contact.postal_code || "",
      };
    }
    // Get employment info from employment object (not disclosures)
    if (accountInfo?.employment) {
      editedDisclosures.employment_status = accountInfo.employment.status || "";
      editedDisclosures.employer_name = accountInfo.employment.employer_name || "";
      editedDisclosures.employer_address = accountInfo.employment.employer_address || "";
      editedDisclosures.employment_position = accountInfo.employment.job_title || "";
    }
    
    // Get affiliations from disclosures object
    if (accountInfo?.disclosures) {
      editedDisclosures.is_control_person = accountInfo.disclosures.is_control_person || false;
      editedDisclosures.is_affiliated_exchange_or_finra = accountInfo.disclosures.is_affiliated_exchange_or_finra || false;
      editedDisclosures.is_politically_exposed = accountInfo.disclosures.is_politically_exposed || false;
      editedDisclosures.immediate_family_exposed = accountInfo.disclosures.immediate_family_exposed || false;
    }
    
    // Get funding source from financial object (not identity)
    if (accountInfo?.financial) {
      const fundingSource = accountInfo.financial.funding_source;
      editedIdentity.funding_source = Array.isArray(fundingSource) ? fundingSource : (fundingSource ? [fundingSource] : []);
    }
  }

  async function savePersonalInfo() {
    personalInfoError = "";
    personalInfoSuccess = "";
    personalFieldErrors = {};
    
    // Run validation first
    if (!validatePersonalInfo()) {
      personalInfoError = "Please correct the errors below.";
      return;
    }
    
    personalInfoSaving = true;
    
    try {
      const token = localStorage.getItem("access_token");
      
      // Build update payload with only changed fields
      const updatePayload = {};
      
      // Check if contact info changed
      const contactChanges = {};
      const displayPhone = stripPhonePrefix(accountInfo.contact?.phone_number);
      if (editedContact.phone_number !== displayPhone) {
        // Prepend +1 for US phone numbers
        const cleanPhone = editedContact.phone_number.replace(/\D/g, '');
        contactChanges.phone_number = "+1" + cleanPhone;
      }
      
      const currentStreet = Array.isArray(accountInfo.contact?.street_address)
        ? accountInfo.contact.street_address.join(", ")
        : (accountInfo.contact?.street_address || "");
      if (editedContact.street_address !== currentStreet) {
        contactChanges.street_address = editedContact.street_address.split(",").map(s => s.trim()).filter(s => s);
      }
      
      if (editedContact.unit !== (accountInfo.contact?.unit || "")) {
        contactChanges.unit = editedContact.unit || null;
      }
      if (editedContact.city !== (accountInfo.contact?.city || "")) {
        contactChanges.city = editedContact.city;
      }
      if (editedContact.state !== (accountInfo.contact?.state || "")) {
        contactChanges.state = editedContact.state;
      }
      if (editedContact.postal_code !== (accountInfo.contact?.postal_code || "")) {
        contactChanges.postal_code = editedContact.postal_code;
      }
      
      if (Object.keys(contactChanges).length > 0) {
        updatePayload.contact = contactChanges;
      }
      
      // Check if employment info changed (stored in employment object, not disclosures)
      const employmentChanges = {};
      if (editedDisclosures.employment_status !== (accountInfo.employment?.status || "")) {
        employmentChanges.status = editedDisclosures.employment_status || null;
      }
      if (editedDisclosures.employer_name !== (accountInfo.employment?.employer_name || "")) {
        employmentChanges.employer_name = editedDisclosures.employer_name || null;
      }
      if (editedDisclosures.employer_address !== (accountInfo.employment?.employer_address || "")) {
        employmentChanges.employer_address = editedDisclosures.employer_address || null;
      }
      if (editedDisclosures.employment_position !== (accountInfo.employment?.job_title || "")) {
        employmentChanges.job_title = editedDisclosures.employment_position || null;
      }
      
      // Check if regulatory disclosures changed
      const disclosureChanges = {};
      if (editedDisclosures.is_control_person !== (accountInfo.disclosures?.is_control_person || false)) {
        disclosureChanges.is_control_person = editedDisclosures.is_control_person;
      }
      if (editedDisclosures.is_affiliated_exchange_or_finra !== (accountInfo.disclosures?.is_affiliated_exchange_or_finra || false)) {
        disclosureChanges.is_affiliated_exchange_or_finra = editedDisclosures.is_affiliated_exchange_or_finra;
      }
      if (editedDisclosures.is_politically_exposed !== (accountInfo.disclosures?.is_politically_exposed || false)) {
        disclosureChanges.is_politically_exposed = editedDisclosures.is_politically_exposed;
      }
      if (editedDisclosures.immediate_family_exposed !== (accountInfo.disclosures?.immediate_family_exposed || false)) {
        disclosureChanges.immediate_family_exposed = editedDisclosures.immediate_family_exposed;
      }
      
      // Add employment changes to payload
      if (Object.keys(employmentChanges).length > 0) {
        updatePayload.employment = employmentChanges;
      }
      
      // Add disclosure changes to payload
      if (Object.keys(disclosureChanges).length > 0) {
        updatePayload.disclosures = disclosureChanges;
      }
      
      // Check if financial (funding source) changed
      const currentFunding = accountInfo.financial?.funding_source;
      const currentFundingArray = Array.isArray(currentFunding) ? currentFunding : (currentFunding ? [currentFunding] : []);
      const editedFunding = editedIdentity.funding_source || [];
      const fundingChanged = JSON.stringify([...currentFundingArray].sort()) !== JSON.stringify([...editedFunding].sort());
      if (fundingChanged && editedFunding.length > 0) {
        updatePayload.financial = { funding_source: editedFunding };
      }
      
      if (Object.keys(updatePayload).length === 0) {
        personalInfoSuccess = "No changes to save.";
        personalInfoEditMode = false;
        setTimeout(() => { personalInfoSuccess = ""; }, 3000);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/account/info`, {
        method: "PATCH",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatePayload),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to update information");
      }
      
      personalInfoSuccess = "Personal information updated successfully!";
      personalInfoEditMode = false;
      
      // Refresh the account info
      await fetchPersonalInfo();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        personalInfoSuccess = "";
      }, 3000);
    } catch (err) {
      personalInfoError = err.message;
    } finally {
      personalInfoSaving = false;
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString();
  }

  function handleImageError() {
    imageError = true;
  }

  // Reset image error when user changes
  $: if ($user) {
    imageError = false;
  }

  async function checkClosureEligibility() {
    closureLoading = true;
    closureError = null;
    closureEligibility = null;
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_URL}/api/account/closure-check`, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error("Failed to check account closure eligibility");
      }
      
      closureEligibility = await response.json();
    } catch (err) {
      closureError = err.message;
    } finally {
      closureLoading = false;
    }
  }

  async function openCloseModal() {
    showCloseModal = true;
    confirmText = "";
    closureSuccess = false;
    await checkClosureEligibility();
  }

  function closeModal() {
    showCloseModal = false;
    confirmText = "";
    closureError = null;
    closureSuccess = false;
  }

  async function closeAccount() {
    if (confirmText !== "CLOSE MY ACCOUNT") {
      closureError = "Please type 'CLOSE MY ACCOUNT' to confirm";
      return;
    }
    
    closureLoading = true;
    closureError = null;
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_URL}/api/account/close`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to close account");
      }
      
      closureSuccess = true;
      
      // Log out after a delay
      setTimeout(() => {
        auth.logout();
        navigate("/");
      }, 3000);
      
    } catch (err) {
      closureError = err.message;
    } finally {
      closureLoading = false;
    }
  }

  function formatCurrency(amount) {
    if (!amount) return "$0.00";
    const num = parseFloat(amount);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  }
</script>
<header class="mb-6">
  <h1 class="text-2xl font-bold text-ink">Settings</h1>
  <p class="text-sm text-ink-secondary mt-1">Manage your account, personal information, and investment preferences.</p>
</header>

<div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
  <div class="lg:col-span-2 space-y-6">
    <!-- Account Card -->
    <section class="card card-pad">
      <h2 class="text-lg font-semibold text-ink mb-4">Account</h2>

      <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-3">User information</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="label">Name</label>
          <input type="text" value={$user?.name || ""} disabled class="input" />
        </div>
        <div>
          <label class="label">Email</label>
          <input type="email" value={$user?.email || ""} disabled class="input" />
        </div>
      </div>

      <hr class="border-line my-6" />

      <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-3">Account status</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="label">Account created</label>
          <input type="text" value={formatDate($user?.created_at)} disabled class="input tnum" />
        </div>
        <div>
          <span class="label">Brokerage account</span>
          <div class="py-2.5">
            <span class="badge {$user?.alpaca_connected ? 'badge-success' : 'badge-neutral'}">
              {$user?.alpaca_connected ? "Connected" : "Not Connected"}
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Investment Settings Card -->
    {#if $hasAlpacaAccount}
      <section class="card card-pad">
        <h2 class="text-lg font-semibold text-ink mb-4">Investment settings</h2>

        {#if settingsLoading}
          <div class="space-y-4">
            <div class="skeleton h-20"></div>
            <div class="skeleton h-20"></div>
          </div>
        {:else}
          {#if settingsError}
            <div class="banner banner-danger mb-4">
              <i class="fas fa-exclamation-circle mr-2"></i>
              {settingsError}
            </div>
          {/if}

          {#if settingsSuccess}
            <div class="banner banner-success mb-4">
              <i class="fas fa-check-circle mr-2"></i>
              {settingsSuccess}
            </div>
          {/if}

          <!-- Auto-Rebalance Toggle -->
          <div class="flex items-start justify-between gap-4 rounded-xl border border-line p-4 mb-4">
            <div class="flex-1">
              <h3 class="text-base font-medium text-ink">Auto-rebalance</h3>
              <p class="text-sm text-ink-secondary mt-1">
                Automatically rebalance your portfolio when you change pie allocations.
                When disabled, you'll need to manually click "Rebalance" to adjust positions.
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
              <input
                type="checkbox"
                bind:checked={investmentSettings.auto_rebalance_enabled}
                class="sr-only peer"
                disabled={settingsSaving}
              />
              <div class="w-11 h-6 bg-line rounded-full peer peer-focus:ring-2 peer-focus:ring-moira peer-checked:bg-moira after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
            </label>
          </div>

          <!-- Smart Investing Info -->
          <div class="rounded-xl bg-canvas border border-line p-4">
            <h3 class="text-base font-medium text-ink">
              <i class="fas fa-lightbulb text-ink-tertiary mr-2"></i>
              Smart investing
            </h3>
            <p class="text-sm text-ink-secondary mt-1">
              When you invest in your pies, Moira Finance automatically prioritizes buying
              underweight positions first to keep your portfolio balanced. This feature is
              always enabled for optimal portfolio allocation.
            </p>
          </div>

          <!-- Save Button -->
          <div class="mt-6 flex justify-end">
            <button
              on:click={saveSettings}
              disabled={settingsSaving}
              class="btn btn-primary"
            >
              {#if settingsSaving}
                <i class="fas fa-spinner fa-spin"></i>
                Saving...
              {:else}
                Save settings
              {/if}
            </button>
          </div>
        {/if}
      </section>
    {/if}

    <!-- Personal Information Card -->
    {#if $hasAlpacaAccount}
      <section class="card card-pad">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-ink">Personal information</h2>
          {#if !personalInfoEditMode && !personalInfoLoading}
            <button
              on:click={enableEditMode}
              class="btn btn-outline btn-sm"
            >
              <i class="fas fa-pen text-ink-tertiary"></i>
              Edit
            </button>
          {/if}
        </div>

        {#if personalInfoLoading}
          <div class="space-y-4">
            <div class="skeleton h-24"></div>
            <div class="skeleton h-24"></div>
            <div class="skeleton h-24"></div>
          </div>
        {:else if !accountInfo}
          <div class="text-center py-8">
            <i class="fas fa-exclamation-circle text-2xl text-ink-tertiary"></i>
            <p class="text-sm text-ink-secondary mt-2">Unable to load personal information</p>
          </div>
        {:else}
          {#if personalInfoError}
            <div class="banner banner-danger mb-4">
              <i class="fas fa-exclamation-circle mr-2"></i>
              {personalInfoError}
            </div>
          {/if}

          {#if personalInfoSuccess}
            <div class="banner banner-success mb-4">
              <i class="fas fa-check-circle mr-2"></i>
              {personalInfoSuccess}
            </div>
          {/if}

          <!-- Identity Information (Read-only) -->
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-3">Identity</p>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="label">First name</label>
              <input
                type="text"
                value={accountInfo.identity?.given_name || ""}
                disabled
                class="input"
              />
            </div>
            <div>
              <label class="label">Middle name</label>
              <input
                type="text"
                value={accountInfo.identity?.middle_name || "—"}
                disabled
                class="input"
              />
            </div>
            <div>
              <label class="label">Last name</label>
              <input
                type="text"
                value={accountInfo.identity?.family_name || ""}
                disabled
                class="input"
              />
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label class="label">Date of birth</label>
              <input
                type="text"
                value={accountInfo.identity?.date_of_birth || ""}
                disabled
                class="input tnum"
              />
            </div>
            <div>
              <label class="label">Citizenship</label>
              <input
                type="text"
                value={accountInfo.identity?.country_of_citizenship || "USA"}
                disabled
                class="input"
              />
            </div>
          </div>

          <p class="text-xs text-ink-tertiary mt-3">
            <i class="fas fa-info-circle mr-1"></i>
            Identity information (name, date of birth) cannot be changed after account creation. Contact support if you need to update this information.
          </p>

          <hr class="border-line my-6" />

          <!-- Contact Information -->
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-3">Contact</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="label">Email</label>
              <input
                type="email"
                value={accountInfo.contact?.email_address || ""}
                disabled
                class="input"
              />
              <p class="text-xs text-ink-tertiary mt-1.5">Email is managed through Google Sign-In</p>
            </div>
            <div>
              <label class="label">Phone number</label>
              {#if personalInfoEditMode}
                <div class="flex">
                  <span class="inline-flex items-center px-3 text-sm text-ink-secondary bg-surface-variant rounded-l-xl">+1</span>
                  <input
                    type="tel"
                    bind:value={editedContact.phone_number}
                    on:input={handlePhoneInput}
                    placeholder="(555) 123-4567"
                    class="input rounded-l-none flex-1 {personalFieldErrors.phone_number ? 'input-error' : ''}"
                  />
                </div>
                {#if personalFieldErrors.phone_number}
                  <p class="help-error">{personalFieldErrors.phone_number}</p>
                {/if}
              {:else}
                <div class="flex">
                  <span class="inline-flex items-center px-3 text-sm text-ink-secondary bg-surface-variant rounded-l-xl">+1</span>
                  <input
                    type="text"
                    value={stripPhonePrefix(accountInfo.contact?.phone_number)}
                    disabled
                    class="input rounded-l-none flex-1 tnum"
                  />
                </div>
              {/if}
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div class="md:col-span-2">
              <label class="label">Street address</label>
              {#if personalInfoEditMode}
                <input
                  type="text"
                  bind:value={editedContact.street_address}
                  placeholder="123 Main St"
                  class="input {personalFieldErrors.street_address ? 'input-error' : ''}"
                />
                {#if personalFieldErrors.street_address}
                  <p class="help-error">{personalFieldErrors.street_address}</p>
                {/if}
              {:else}
                <input
                  type="text"
                  value={Array.isArray(accountInfo.contact?.street_address) ? accountInfo.contact.street_address.join(", ") : (accountInfo.contact?.street_address || "")}
                  disabled
                  class="input"
                />
              {/if}
            </div>
            <div>
              <label class="label">Unit/Apt</label>
              {#if personalInfoEditMode}
                <input
                  type="text"
                  bind:value={editedContact.unit}
                  placeholder="Apt 4B"
                  class="input"
                />
              {:else}
                <input
                  type="text"
                  value={accountInfo.contact?.unit || "—"}
                  disabled
                  class="input"
                />
              {/if}
            </div>
            <div>
              <label class="label">City</label>
              {#if personalInfoEditMode}
                <input
                  type="text"
                  bind:value={editedContact.city}
                  placeholder="New York"
                  class="input {personalFieldErrors.city ? 'input-error' : ''}"
                />
                {#if personalFieldErrors.city}
                  <p class="help-error">{personalFieldErrors.city}</p>
                {/if}
              {:else}
                <input
                  type="text"
                  value={accountInfo.contact?.city || ""}
                  disabled
                  class="input"
                />
              {/if}
            </div>
            <div>
              <label class="label">State</label>
              {#if personalInfoEditMode}
                <select
                  bind:value={editedContact.state}
                  class="select {personalFieldErrors.state ? 'input-error' : ''}"
                >
                  <option value="">Select State</option>
                  {#each usStates as state}
                    <option value={state.code}>{state.code} - {state.name}</option>
                  {/each}
                </select>
                {#if personalFieldErrors.state}
                  <p class="help-error">{personalFieldErrors.state}</p>
                {/if}
              {:else}
                <input
                  type="text"
                  value={accountInfo.contact?.state || ""}
                  disabled
                  class="input"
                />
              {/if}
            </div>
            <div>
              <label class="label">ZIP code</label>
              {#if personalInfoEditMode}
                <input
                  type="text"
                  bind:value={editedContact.postal_code}
                  placeholder="10001"
                  class="input tnum {personalFieldErrors.postal_code ? 'input-error' : ''}"
                />
                {#if personalFieldErrors.postal_code}
                  <p class="help-error">{personalFieldErrors.postal_code}</p>
                {/if}
              {:else}
                <input
                  type="text"
                  value={accountInfo.contact?.postal_code || ""}
                  disabled
                  class="input tnum"
                />
              {/if}
            </div>
          </div>

          <hr class="border-line my-6" />

          <!-- Employment Information -->
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-3">Employment</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="label">Employment status</label>
              {#if personalInfoEditMode}
                <select
                  bind:value={editedDisclosures.employment_status}
                  class="select"
                >
                  <option value="">Select...</option>
                  <option value="employed">Employed</option>
                  <option value="self_employed">Self-Employed</option>
                  <option value="unemployed">Unemployed</option>
                  <option value="retired">Retired</option>
                  <option value="student">Student</option>
                </select>
              {:else}
                <input
                  type="text"
                  value={accountInfo.employment?.status || "—"}
                  disabled
                  class="input capitalize"
                />
              {/if}
            </div>
            <div>
              <label class="label">Employer name</label>
              {#if personalInfoEditMode}
                <input
                  type="text"
                  bind:value={editedDisclosures.employer_name}
                  placeholder="Company Inc."
                  class="input"
                />
              {:else}
                <input
                  type="text"
                  value={accountInfo.employment?.employer_name || "—"}
                  disabled
                  class="input"
                />
              {/if}
            </div>
            <div>
              <label class="label">Job title</label>
              {#if personalInfoEditMode}
                <input
                  type="text"
                  bind:value={editedDisclosures.employment_position}
                  placeholder="Software Engineer"
                  class="input"
                />
              {:else}
                <input
                  type="text"
                  value={accountInfo.employment?.job_title || "—"}
                  disabled
                  class="input"
                />
              {/if}
            </div>
            <div>
              <label class="label">Employer address</label>
              {#if personalInfoEditMode}
                <input
                  type="text"
                  bind:value={editedDisclosures.employer_address}
                  placeholder="123 Business Ave, City, ST"
                  class="input"
                />
              {:else}
                <input
                  type="text"
                  value={accountInfo.employment?.employer_address || "—"}
                  disabled
                  class="input"
                />
              {/if}
            </div>
          </div>

          <hr class="border-line my-6" />

          <!-- Funding Sources -->
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-3">Funding sources</p>
          <div>
            <label class="label">Source of funds</label>
            {#if personalInfoEditMode}
              <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
                {#each fundingSources as source}
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editedIdentity.funding_source.includes(source.value)}
                      on:change={(e) => {
                        if (e.target.checked) {
                          editedIdentity.funding_source = [...editedIdentity.funding_source, source.value];
                        } else {
                          editedIdentity.funding_source = editedIdentity.funding_source.filter(s => s !== source.value);
                        }
                      }}
                      class="checkbox"
                    />
                    <span class="text-sm text-ink">{source.label}</span>
                  </label>
                {/each}
              </div>
            {:else}
              <div class="flex flex-wrap gap-2">
                {#if accountInfo.financial?.funding_source}
                  {@const fundingSource = accountInfo.financial.funding_source}
                  {@const sources = Array.isArray(fundingSource) ? fundingSource : [fundingSource]}
                  {#each sources as source}
                    <span class="badge bg-indigo-50 text-indigo-700 capitalize">
                      {source.replace(/_/g, ' ')}
                    </span>
                  {/each}
                {:else}
                  <span class="text-sm text-ink-tertiary">—</span>
                {/if}
              </div>
            {/if}
          </div>

          <hr class="border-line my-6" />

          <!-- Regulatory Disclosures -->
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-2">Regulatory disclosures</p>
          <p class="text-xs text-ink-tertiary mb-4">
            <i class="fas fa-info-circle mr-1"></i>
            Update these if your circumstances have changed since opening your account.
          </p>
          <div>
            {#if personalInfoEditMode}
              <div class="space-y-4">
                <label class="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    bind:checked={editedDisclosures.is_control_person}
                    class="checkbox mt-0.5"
                  />
                  <span class="text-sm text-ink">
                    I am a control person of a publicly traded company
                    <span class="block text-xs text-ink-tertiary mt-1">
                      (Director, officer, or 10%+ shareholder of a publicly traded company)
                    </span>
                  </span>
                </label>

                <label class="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    bind:checked={editedDisclosures.is_affiliated_exchange_or_finra}
                    class="checkbox mt-0.5"
                  />
                  <span class="text-sm text-ink">
                    I am affiliated with a stock exchange or FINRA
                    <span class="block text-xs text-ink-tertiary mt-1">
                      (Employee of a broker-dealer or securities exchange)
                    </span>
                  </span>
                </label>

                <label class="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    bind:checked={editedDisclosures.is_politically_exposed}
                    class="checkbox mt-0.5"
                  />
                  <span class="text-sm text-ink">
                    I am a politically exposed person
                    <span class="block text-xs text-ink-tertiary mt-1">
                      (Senior government official, executive of a state-owned enterprise, or prominent political figure)
                    </span>
                  </span>
                </label>

                <label class="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    bind:checked={editedDisclosures.immediate_family_exposed}
                    class="checkbox mt-0.5"
                  />
                  <span class="text-sm text-ink">
                    An immediate family member is a politically exposed person
                    <span class="block text-xs text-ink-tertiary mt-1">
                      (Spouse, parent, sibling, or child of a politically exposed person)
                    </span>
                  </span>
                </label>
              </div>
            {:else}
              <div class="space-y-2">
                <div class="flex items-center gap-2">
                  <i class="fas {accountInfo.disclosures?.is_control_person ? 'fa-check-circle text-moira' : 'fa-times-circle text-ink-tertiary'}"></i>
                  <span class="text-sm text-ink-secondary">Control person of publicly traded company</span>
                </div>
                <div class="flex items-center gap-2">
                  <i class="fas {accountInfo.disclosures?.is_affiliated_exchange_or_finra ? 'fa-check-circle text-moira' : 'fa-times-circle text-ink-tertiary'}"></i>
                  <span class="text-sm text-ink-secondary">Affiliated with exchange or FINRA</span>
                </div>
                <div class="flex items-center gap-2">
                  <i class="fas {accountInfo.disclosures?.is_politically_exposed ? 'fa-check-circle text-moira' : 'fa-times-circle text-ink-tertiary'}"></i>
                  <span class="text-sm text-ink-secondary">Politically exposed person</span>
                </div>
                <div class="flex items-center gap-2">
                  <i class="fas {accountInfo.disclosures?.immediate_family_exposed ? 'fa-check-circle text-moira' : 'fa-times-circle text-ink-tertiary'}"></i>
                  <span class="text-sm text-ink-secondary">Family member is politically exposed</span>
                </div>
              </div>
            {/if}
          </div>

          <!-- Edit Mode Buttons -->
          {#if personalInfoEditMode}
            <div class="mt-6 flex justify-end gap-3">
              <button
                on:click={cancelEdit}
                disabled={personalInfoSaving}
                class="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                on:click={savePersonalInfo}
                disabled={personalInfoSaving}
                class="btn btn-primary"
              >
                {#if personalInfoSaving}
                  <i class="fas fa-spinner fa-spin"></i>
                  Saving...
                {:else}
                  Save changes
                {/if}
              </button>
            </div>
          {/if}
        {/if}
      </section>
    {/if}
  </div>

  <!-- Profile Card -->
  <div class="space-y-6">
    <div class="card card-pad text-center">
      <div class="flex justify-center">
        {#if $user?.picture && !imageError}
          <img
            alt="User avatar"
            src={$user.picture}
            class="rounded-full h-24 w-24 object-cover border border-line"
            on:error={handleImageError}
          />
        {:else}
          <div class="rounded-full h-24 w-24 bg-surface-variant flex items-center justify-center">
            <i class="fas fa-user text-3xl text-ink-tertiary"></i>
          </div>
        {/if}
      </div>
      <h3 class="text-lg font-semibold text-ink mt-4">
        {$user?.name || "User"}
      </h3>
      <p class="text-sm text-ink-secondary mt-1">
        {$user?.email || ""}
      </p>
      <div class="mt-6 pt-6 border-t border-line">
        <p class="text-sm text-ink-secondary">
          Your account is managed through Google Sign-In.
          Contact support for any account-related inquiries.
        </p>
      </div>
    </div>
  </div>
</div>

<!-- Danger Zone Section -->
{#if $hasAlpacaAccount}
  <section class="card card-pad border-red-200 mt-6">
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div class="flex-1">
        <h2 class="text-lg font-semibold text-ink">Danger zone</h2>
        <p class="text-sm text-ink-secondary mt-1">
          Permanently close your brokerage account. This action cannot be undone.
          You must liquidate all positions and withdraw all funds before closing.
        </p>
      </div>
      <button
        on:click={openCloseModal}
        class="btn btn-danger btn-sm shrink-0"
      >
        Close account
      </button>
    </div>
  </section>
{/if}

<!-- Close Account Modal -->
{#if showCloseModal}
  <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-center justify-center min-h-screen p-4">
      <!-- Background overlay -->
      <div class="fixed inset-0 bg-slate-900/50 transition-opacity" on:click={closeModal}></div>

      <!-- Modal panel -->
      <div class="relative bg-surface border border-line rounded-2xl shadow-sm w-full max-w-lg overflow-hidden text-left">
        <div class="p-6">
          <div class="flex items-start gap-4">
            <div class="shrink-0 w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
              <i class="fas fa-exclamation-triangle text-danger"></i>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-lg font-semibold text-ink" id="modal-title">
                Close your account
              </h3>

              {#if closureLoading && !closureEligibility}
                <div class="mt-4 space-y-3">
                  <div class="skeleton h-16"></div>
                  <div class="skeleton h-10"></div>
                </div>
              {:else if closureSuccess}
                <div class="mt-4">
                  <div class="banner banner-success text-center">
                    <i class="fas fa-check-circle text-2xl"></i>
                    <p class="font-semibold mt-2">Account Closed Successfully</p>
                    <p class="text-sm mt-1">You will be logged out shortly...</p>
                  </div>
                </div>
              {:else if closureEligibility}
                {#if closureEligibility.can_close}
                  <div class="mt-4">
                    <div class="banner banner-success mb-4">
                      <i class="fas fa-check-circle mr-2"></i>
                      Your account is eligible for closure.
                    </div>

                    <div class="banner banner-danger mb-4">
                      <p class="font-semibold mb-2">
                        <i class="fas fa-exclamation-circle mr-2"></i>
                        Warning: This action is permanent
                      </p>
                      <ul class="list-disc list-inside">
                        <li>Your brokerage account will be permanently closed</li>
                        <li>All account history will be deleted</li>
                        <li>This action cannot be undone</li>
                      </ul>
                    </div>

                    <div class="mt-4">
                      <label class="label">
                        To confirm, type "CLOSE MY ACCOUNT"
                      </label>
                      <input
                        type="text"
                        bind:value={confirmText}
                        placeholder="CLOSE MY ACCOUNT"
                        class="input"
                      />
                    </div>

                    {#if closureError}
                      <div class="banner banner-danger mt-3">
                        {closureError}
                      </div>
                    {/if}
                  </div>
                {:else}
                  <div class="mt-4">
                    <div class="banner banner-warning mb-4">
                      <p class="font-semibold mb-1">
                        <i class="fas fa-exclamation-triangle mr-2"></i>
                        Your account cannot be closed yet
                      </p>
                      <p>Please complete the following before closing:</p>
                    </div>

                    <ul class="space-y-3">
                      {#each closureEligibility.reasons as reason}
                        <li class="flex items-start rounded-xl bg-surface-variant p-3">
                          <i class="fas fa-times-circle text-danger mr-3 mt-0.5"></i>
                          <span class="text-sm text-ink">{reason}</span>
                        </li>
                      {/each}
                    </ul>

                    {#if closureEligibility.positions_count > 0}
                      <div class="banner banner-info mt-4">
                        <i class="fas fa-info-circle mr-2"></i>
                        You have <strong class="tnum">{closureEligibility.positions_count}</strong> open position(s).
                        Go to <a href="/admin/portfolio" class="font-medium underline">Portfolio</a> to sell them.
                      </div>
                    {/if}

                    {#if closureEligibility.cash_balance && parseFloat(closureEligibility.cash_balance) > 0}
                      <div class="banner banner-info mt-3">
                        <i class="fas fa-info-circle mr-2"></i>
                        Cash balance: <strong class="tnum">{formatCurrency(closureEligibility.cash_balance)}</strong>.
                        Go to <a href="/admin/banking" class="font-medium underline">Banking</a> to withdraw funds.
                      </div>
                    {/if}
                  </div>
                {/if}
              {:else if closureError}
                <div class="banner banner-danger mt-4">
                  {closureError}
                </div>
              {/if}
            </div>
          </div>
        </div>

        <div class="bg-canvas border-t border-line px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          {#if !closureSuccess}
            <button
              type="button"
              on:click={closeModal}
              class="btn btn-outline btn-sm"
            >
              Cancel
            </button>
            {#if closureEligibility?.can_close}
              <button
                type="button"
                on:click={closeAccount}
                disabled={closureLoading || confirmText !== "CLOSE MY ACCOUNT"}
                class="btn btn-danger btn-sm"
              >
                {#if closureLoading}
                  <i class="fas fa-spinner fa-spin"></i>
                {/if}
                Close account permanently
              </button>
            {/if}
          {/if}
        </div>
      </div>
    </div>
  </div>
{/if}
